import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, Sparkles, Zap, TrendingUp } from "lucide-react";
import { waLink, WA_DEFAULT_MESSAGE, IMAGES } from "../config";

const LINES = [
    { text: "Tecnologia,", accent: false },
    { text: "Intelligenza Artificiale", accent: true },
    { text: "Performance.", accent: false },
];

const METRICS = [
    { value: "100+", label: "Aziende affiancate" },
    { value: "5x", label: "Produttività con l'IA" },
    { value: "25k+", label: "Community Creator" },
];

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const portraitRotate = useTransform(scrollYProgress, [0, 1], [0, 4]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-24"
        >
            <div className="hero-grid-bg absolute inset-0" aria-hidden="true" />
            <div
                className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[#48CAE4]/25 blur-3xl"
                aria-hidden="true"
            />
            <div
                className="absolute top-1/2 -left-40 h-[420px] w-[420px] rounded-full bg-[#0077B6]/15 blur-3xl"
                aria-hidden="true"
            />
            <img
                src={IMAGES.aiWave}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover opacity-[0.06]"
            />

            <div className="relative mx-auto max-w-6xl px-5 sm:px-8 grid gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
                <motion.div style={{ y: textY }}>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-white/80 border border-sky-200 text-[#0077B6] shadow-sm"
                        data-testid="hero-badge"
                    >
                        <Sparkles className="h-3.5 w-3.5" />
                        Maria Fasanella · Creator Plus
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#03045E]"
                    >
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.accent ? "text-gradient-azure" : ""}`}
                                    initial={{ y: "115%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        data-testid="hero-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
                        className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
                    >
                        Aiuto Creator e aziende a trasformare l'Intelligenza Artificiale
                        in risultati concreti: più velocità, più qualità, nuove
                        opportunità di crescita nel mondo digitale.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
                        className="mt-8 flex flex-col sm:flex-row gap-3.5"
                    >
                        <a
                            href={waLink(WA_DEFAULT_MESSAGE)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full text-base font-semibold text-white bg-[#25D366] hover:bg-[#1fb857] shadow-xl shadow-emerald-500/25 transition-all duration-300 hover:-translate-y-0.5"
                        >
                            <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                            Contattami su WhatsApp
                        </a>
                        <a
                            href="#progetti"
                            data-testid="hero-projects-button"
                            className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-full text-base font-semibold text-[#03045E] bg-white border border-slate-200 hover:border-[#48CAE4] hover:text-[#0077B6] shadow-lg shadow-sky-950/5 transition-all duration-300 hover:-translate-y-0.5"
                        >
                            <ArrowDown className="h-5 w-5 transition-transform duration-300 group-hover:translate-y-0.5" />
                            Scopri i miei progetti
                        </a>
                    </motion.div>

                    <motion.dl
                        data-testid="hero-metrics"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="mt-12 grid grid-cols-3 gap-4 border-t border-slate-200 pt-7 max-w-lg"
                    >
                        {METRICS.map((m) => (
                            <div key={m.label}>
                                <dt className="sr-only">{m.label}</dt>
                                <dd className="font-display text-2xl sm:text-3xl font-bold text-[#0077B6]">
                                    {m.value}
                                </dd>
                                <dd className="mt-1 text-xs sm:text-sm text-slate-500 leading-snug">
                                    {m.label}
                                </dd>
                            </div>
                        ))}
                    </motion.dl>
                </motion.div>

                <motion.div
                    style={{ y: portraitY, rotate: portraitRotate }}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.4, ease: EASE }}
                    className="relative mx-auto w-full max-w-sm lg:max-w-none"
                    data-testid="hero-portrait-card"
                >
                    <div className="relative rounded-[2rem] overflow-hidden border-4 border-white shadow-2xl shadow-sky-900/20">
                        <img
                            src={IMAGES.portrait}
                            alt="Maria Fasanella — Creator Plus"
                            className="w-full aspect-[4/5] object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#03045E]/45 via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl px-4 py-3 flex items-center gap-3">
                            <span className="relative flex h-3 w-3">
                                <span className="animate-pulse-dot absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                            </span>
                            <p className="text-sm font-semibold text-[#03045E]">
                                Disponibile per nuovi progetti
                            </p>
                        </div>
                    </div>

                    <div className="animate-float-slow absolute -top-5 -left-4 sm:-left-8 glass rounded-2xl px-4 py-3 shadow-xl shadow-sky-900/10 flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0077B6] text-white">
                            <Zap className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="text-xs font-bold text-[#03045E]">AI Performance</p>
                            <p className="text-[10px] text-slate-500">Workflow automatizzati</p>
                        </div>
                    </div>

                    <div
                        className="animate-float-slow absolute -top-5 -right-3 sm:-right-6 glass rounded-2xl px-4 py-3 shadow-xl shadow-sky-900/10 flex items-center gap-2.5"
                        style={{ animationDelay: "1.6s" }}
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#48CAE4] text-[#03045E]">
                            <TrendingUp className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="text-xs font-bold text-[#03045E]">Creator Plus</p>
                            <p className="text-[10px] text-slate-500">Crescita digitale</p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
